import React from 'react';
import { Circle, CircleCheck, Clock, Package, RefreshCw, Truck, X } from 'lucide-react';

import { formatIST } from '@/lib/utils';
import { SelectField } from '@/components/phone/FieldControls';
import {
  EmptyState, IconButton, PageHeader, Pager, PhonePage, ScreenLoader, SearchBar, StatusPill, TabRow,
} from '@/components/phone/primitives';
import { Colors, pcs } from '@/components/phone/theme';

/**
 * Plates, on a phone.
 *
 * The desktop grid has eleven columns; a card says which job the plate is for,
 * where it stands, and how long it has been waiting. The four states are tabs,
 * the sort is a sheet, and marking is either one button on the card or — since
 * plates go out to one vendor in batches — a tick per card and a bar at the
 * thumb that marks the ticked ones together.
 */

const STATUS_TONE = { pending: 'caution', ordered: 'accent', received: 'positive' };
const STATUS_LABEL = { pending: 'Pending', ordered: 'Ordered', received: 'Received' };

function PlateCard({ plate, canMark, picked, onPick, busy, onOrdered, onReceived, onOpen }) {
  const status = plate.plate_status || 'pending';
  const waiting = status !== 'received' && plate.days_waiting > 0;
  const PickIcon = picked ? CircleCheck : Circle;
  return (
    <li
      data-testid={`plate-card-${plate.line_item_id}`}
      className="mt-3 rounded-[10px] border"
      style={{
        backgroundColor: picked ? Colors.accent.wash : Colors.background.primary,
        borderColor: picked ? Colors.accent.DEFAULT : Colors.border.default,
      }}
    >
      <div className="flex items-start gap-2 px-3 pt-3">
        {canMark && status !== 'received' && (
          <button
            type="button"
            onClick={onPick}
            aria-pressed={picked}
            aria-label={`Select ${plate.item_name || plate.order_id}`}
            className="flex h-11 w-11 shrink-0 items-center justify-center"
          >
            <PickIcon className="h-6 w-6" style={{ color: picked ? Colors.accent.DEFAULT : Colors.text.disabled }} />
          </button>
        )}
        <button type="button" onClick={onOpen} data-testid={`open-plate-${plate.line_item_id}`} className="min-w-0 flex-1 text-left">
          <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
            {plate.item_name || '—'}
          </span>
          <span className="mt-0.5 block truncate text-[12px] font-medium" style={{ color: Colors.text.secondary }}>
            {plate.customer_name}{plate.firm_name && plate.firm_name !== plate.customer_name ? ` — ${plate.firm_name}` : ''}
          </span>
          <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
            {plate.order_id}{plate.product_type ? ` · ${plate.product_type}` : ''}{plate.quantity ? ` · ${pcs(plate.quantity)}` : ''}
          </span>
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 pb-3 pt-2">
        <StatusPill label={STATUS_LABEL[status] || status} tone={STATUS_TONE[status] || 'neutral'} dot />
        {plate.holding_production && status !== 'received' && <StatusPill label="holding" tone="critical" />}
        {waiting && (
          <span
            className="text-[12px] font-semibold"
            style={{ color: plate.days_waiting > 3 ? Colors.status.error : plate.days_waiting > 1 ? Colors.semantic.caution : Colors.text.secondary }}
          >
            {plate.days_waiting}d waiting
          </span>
        )}
      </div>

      <p className="px-4 pb-3 text-[11px]" style={{ color: Colors.text.tertiary }}>
        {plate.plate_ordered_at ? `Ordered ${formatIST(plate.plate_ordered_at, { compact: true })}` : 'Not ordered yet'}
        {plate.plate_received_at ? ` · Received ${formatIST(plate.plate_received_at, { compact: true })}` : ''}
        {plate.delivery_date ? ` · Due ${formatIST(plate.delivery_date, { dateOnly: true })}` : ''}
      </p>

      {canMark && status !== 'received' && (
        <div className="border-t px-3 py-2" style={{ borderColor: Colors.border.subtle }}>
          {status === 'pending' ? (
            <button
              type="button"
              onClick={onOrdered}
              disabled={busy}
              aria-label="Mark ordered"
              className="flex h-11 w-full items-center justify-center gap-1.5 rounded-[10px] text-[13px] font-bold disabled:opacity-60"
              style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
            >
              <Package className="h-4 w-4" /> Mark ordered
            </button>
          ) : (
            <button
              type="button"
              onClick={onReceived}
              disabled={busy}
              aria-label="Mark received"
              className="flex h-11 w-full items-center justify-center gap-1.5 rounded-[10px] text-[13px] font-bold disabled:opacity-60"
              style={{ backgroundColor: Colors.semantic.positive, color: Colors.text.inverse }}
            >
              <Truck className="h-4 w-4" /> Mark received
            </button>
          )}
        </div>
      )}
    </li>
  );
}

export default function PhonePlates({
  plates, loading, total, counts, tabs, tab, onTab, search, onSearch, sorts, sortBy, onSort, onRefresh,
  page, totalPages, onPage, canMark, picked, onPick, onPickAll, pickable, canOrder, canReceive, busy, onMark, onOpen,
}) {
  return (
    <PhonePage testID="plate-tracking-page">
      <PageHeader
        title="Plates"
        subtitle={`${total} plate${total === 1 ? '' : 's'}${counts.overdue > 0 ? ` · ${counts.overdue} waiting over 3d` : ''}`}
        action={<IconButton icon={RefreshCw} onClick={onRefresh} label="Refresh" disabled={loading} />}
      />

      <div className="px-5">
        <SearchBar placeholder="Order, customer, firm…" initialValue={search} onChange={onSearch} testID="plates-search-input" />
        <div className="mt-3">
          <TabRow
            tabs={tabs.map((t) => ({ key: t.key, label: `${t.label} ${counts[t.key] ?? 0}`, testID: `stat-${t.key}` }))}
            active={tab}
            onSelect={onTab}
          />
        </div>
        <div className="mt-2 flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <SelectField name="sort" label="Sort plates by" value={sortBy}
              options={sorts.map(([value, label]) => ({ value, label }))} onChange={onSort} />
          </div>
          {canMark && pickable.length > 0 && (
            <button
              type="button"
              onClick={onPickAll}
              className="h-12 shrink-0 rounded-[10px] border px-3 text-[12px] font-semibold"
              style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default, color: Colors.accent.DEFAULT }}
            >
              {picked.size ? 'Clear' : 'Select all'}
            </button>
          )}
        </div>
      </div>

      <div className={`flex-1 px-5 ${picked.size ? 'pb-28' : 'pb-8'}`}>
        {loading ? (
          <ScreenLoader />
        ) : plates.length === 0 ? (
          <EmptyState icon={Clock} message={tab === 'pending' ? 'No plates waiting to be ordered' : 'No plates here'} />
        ) : (
          <ul data-testid="plates-list">
            {plates.map((plate) => (
              <PlateCard
                key={plate.line_item_id}
                plate={plate}
                canMark={canMark}
                picked={picked.has(plate.line_item_id)}
                onPick={() => onPick(plate.line_item_id)}
                busy={busy}
                onOrdered={() => onMark([plate], 'ordered')}
                onReceived={() => onMark([plate], 'received')}
                onOpen={() => onOpen(plate)}
              />
            ))}
          </ul>
        )}
        <Pager page={page} totalPages={totalPages} onPage={onPage} />
      </div>

      {/* the ticked plates, at the thumb — these six went out today */}
      {picked.size > 0 && (
        <div
          data-testid="plate-selection-bar"
          className="fixed bottom-4 left-1/2 z-30 flex w-[calc(100%-2.5rem)] max-w-[23.5rem] -translate-x-1/2 items-center gap-2 rounded-[14px] px-3 py-2.5 shadow-lg"
          style={{ backgroundColor: Colors.neutral[800] }}
        >
          <button
            type="button"
            onClick={onPickAll}
            aria-label="Clear selection"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10"
          >
            <X className="h-[18px] w-[18px]" style={{ color: Colors.text.inverse }} />
          </button>
          <p className="min-w-0 flex-1 truncate text-[12px] font-semibold" style={{ color: Colors.text.inverse }}>
            {picked.size} selected
          </p>
          {canOrder.length > 0 && (
            <button
              type="button"
              onClick={() => onMark(canOrder, 'ordered')}
              disabled={busy}
              className="shrink-0 rounded-[10px] px-3 py-2 text-[12px] font-bold disabled:opacity-60"
              style={{ backgroundColor: Colors.accent.DEFAULT, color: Colors.text.inverse }}
            >
              Mark {canOrder.length} ordered
            </button>
          )}
          {canReceive.length > 0 && (
            <button
              type="button"
              onClick={() => onMark(canReceive, 'received')}
              disabled={busy}
              className="shrink-0 rounded-[10px] px-3 py-2 text-[12px] font-bold disabled:opacity-60"
              style={{ backgroundColor: Colors.semantic.positive, color: Colors.text.inverse }}
            >
              Mark {canReceive.length} received
            </button>
          )}
        </div>
      )}
    </PhonePage>
  );
}
